import {
  type AutocompleteProps,
  type ComboboxProps,
  Stack,
  Text,
} from "@mantine/core"
import { useCallback, useRef, type ReactElement } from "react"
import { LabelWithTooltip, LazyAutocomplete } from "@tfg/ui"

import { findRelatedProductionSitesByOrganizationId } from "~/gen/api/hooks/useFindRelatedProductionSitesByOrganizationId.ts"
import type { FindRelatedProductionSitesByOrganizationIdResponseDto } from "~/gen/api/types/FindRelatedProductionSitesByOrganizationIdResponseDto.ts"
import type { SchemaFieldRendererProps } from "~/utils/zod/schema-form.types"

const MIN_PRODUCTION_SITE_REPOSITORY_AUTOCOMPLETE_CHARACTERS = 3
const MAX_PRODUCTION_SITE_REPOSITORY_AUTOCOMPLETE_RESULTS = 10

const COMBOBOX_PROPS: ComboboxProps = {
  withinPortal: true,
}

const PRODUCTION_SITE_CREATE_NEW_SENTINEL =
  "__production_site_create_new__" as const

type ProductionSiteRepositoryAutocompleteOption = {
  readonly value: string
  readonly label: string
  readonly suggestedProductionSite: FindRelatedProductionSitesByOrganizationIdResponseDto
}

type ProductionSiteCreateNewOption = {
  readonly value: typeof PRODUCTION_SITE_CREATE_NEW_SENTINEL
  readonly label: string
  readonly kind: "createAnyway"
}

type ProductionSiteAutocompleteOption =
  | ProductionSiteRepositoryAutocompleteOption
  | ProductionSiteCreateNewOption

type RepositoryPrefillSnapshot = {
  readonly parentCompany: unknown
  readonly internalCode: unknown
  readonly street: unknown
  readonly city: unknown
  readonly postalCode: unknown
  readonly longitude: unknown
  readonly latitude: unknown
  readonly countryIso3166: unknown
  readonly registrationIds: unknown
  readonly performedActivities: unknown
}

const normalizeAutocompleteTerm = (value: string): string => {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
}

const doesSuggestionMatchSearch = ({
  suggestion,
  search,
}: {
  readonly suggestion: FindRelatedProductionSitesByOrganizationIdResponseDto
  readonly search: string
}): boolean => {
  const normalizedSearch = normalizeAutocompleteTerm(search)
  if (normalizedSearch.length === 0) {
    return false
  }

  const haystack = normalizeAutocompleteTerm(
    `${suggestion.name} ${formatAddress(suggestion)} ${suggestion.countryIso3166}`
  )
  return haystack.includes(normalizedSearch)
}

const getNameRelevanceRank = ({
  name,
  normalizedSearch,
}: {
  readonly name: string
  readonly normalizedSearch: string
}): number => {
  if (normalizedSearch.length === 0) {
    return 2
  }
  const normalizedName = normalizeAutocompleteTerm(name)
  if (normalizedName.startsWith(normalizedSearch)) {
    return 0
  }
  if (normalizedName.includes(normalizedSearch)) {
    return 1
  }
  return 2
}

const findPrimaryVisibleSuggestionForSearch = ({
  suggestions,
  search,
}: {
  readonly suggestions: readonly FindRelatedProductionSitesByOrganizationIdResponseDto[]
  readonly search: string
}): FindRelatedProductionSitesByOrganizationIdResponseDto | null => {
  const visibleSuggestions = suggestions.filter((suggestion) =>
    doesSuggestionMatchSearch({ suggestion, search })
  )
  if (visibleSuggestions.length === 0) {
    return null
  }

  const normalizedSearch = normalizeAutocompleteTerm(search)
  const rankedSuggestions = [...visibleSuggestions].sort((left, right) => {
    const rankDiff =
      getNameRelevanceRank({
        name: left.name,
        normalizedSearch,
      }) -
      getNameRelevanceRank({
        name: right.name,
        normalizedSearch,
      })
    if (rankDiff !== 0) {
      return rankDiff
    }
    const byName = left.name.localeCompare(right.name)
    if (byName !== 0) {
      return byName
    }
    return left.id.localeCompare(right.id)
  })
  return rankedSuggestions[0] ?? null
}

const hasExactVisibleNameMatch = ({
  search,
  options,
}: {
  readonly search: string
  readonly options: readonly ProductionSiteAutocompleteOption[]
}): boolean => {
  const normalizedSearch = normalizeAutocompleteTerm(search)
  if (normalizedSearch.length === 0) {
    return false
  }

  return options.some((option) => {
    if (!Reflect.has(option, "suggestedProductionSite")) {
      return false
    }
    const suggestion = (option as ProductionSiteRepositoryAutocompleteOption)
      .suggestedProductionSite
    return normalizeAutocompleteTerm(suggestion.name) === normalizedSearch
  })
}

const toOptionalCoordinate = (
  coordinate: number | null | undefined
): number | undefined => {
  if (coordinate === null || coordinate === undefined) {
    return undefined
  }
  return coordinate
}

const formatAddress = (
  productionSite: FindRelatedProductionSitesByOrganizationIdResponseDto
): string => {
  return [
    productionSite.street,
    productionSite.postalCode,
    productionSite.city,
    productionSite.countryIso3166,
  ]
    .filter(
      (part): part is string => typeof part === "string" && part.trim() !== ""
    )
    .join(", ")
}

const formatOptionText = (
  productionSite: FindRelatedProductionSitesByOrganizationIdResponseDto
): string => {
  const address = formatAddress(productionSite)
  return address.length > 0
    ? `${productionSite.name} - ${address}`
    : productionSite.name
}

export const ProductionSiteRepositoryAutocompleteField = ({
  name,
  label,
  tooltip,
  placeholder,
  disabled,
  isRequired,
  error,
  onDuplicateCandidateForCreateNew,
  form,
}: SchemaFieldRendererProps): ReactElement => {
  const selectedRepositorySuggestionNameRef = useRef<string | null>(null)
  const repositoryPrefillSnapshotRef = useRef<RepositoryPrefillSnapshot | null>(
    null
  )

  const nameValueRaw = form.values[name]
  const nameValue = typeof nameValueRaw === "string" ? nameValueRaw : ""

  const toSuggestionOption = useCallback(
    (
      suggestion: FindRelatedProductionSitesByOrganizationIdResponseDto
    ): ProductionSiteRepositoryAutocompleteOption => ({
      value: suggestion.id,
      label: suggestion.name,
      suggestedProductionSite: suggestion,
    }),
    []
  )

  const isProductionSiteSuggestionOption = useCallback(
    (option: unknown): option is ProductionSiteRepositoryAutocompleteOption => {
      if (typeof option !== "object" || option === null) {
        return false
      }
      return Reflect.has(option, "suggestedProductionSite")
    },
    []
  )

  const isCreateAnywayOption = useCallback(
    (option: unknown): option is ProductionSiteCreateNewOption => {
      if (typeof option !== "object" || option === null) {
        return false
      }
      return Reflect.get(option, "kind") === "createAnyway"
    },
    []
  )

  const resetRepositoryRecordSelection = useCallback(
    (nextNameValue: string): void => {
      const hadRepositoryRecordSelection =
        selectedRepositorySuggestionNameRef.current !== null

      selectedRepositorySuggestionNameRef.current = null
      if (hadRepositoryRecordSelection) {
        const snapshot = repositoryPrefillSnapshotRef.current
        if (snapshot !== null) {
          form.setFieldValue("parentCompany", snapshot.parentCompany)
          form.setFieldValue("internalCode", snapshot.internalCode)
          form.setFieldValue("street", snapshot.street)
          form.setFieldValue("city", snapshot.city)
          form.setFieldValue("postalCode", snapshot.postalCode)
          form.setFieldValue("longitude", snapshot.longitude)
          form.setFieldValue("latitude", snapshot.latitude)
          form.setFieldValue("countryIso3166", snapshot.countryIso3166)
          form.setFieldValue("registrationIds", snapshot.registrationIds)
          form.setFieldValue(
            "performedActivities",
            snapshot.performedActivities
          )
        }
      }
      repositoryPrefillSnapshotRef.current = null

      form.setFieldValue(name, nextNameValue)
      form.setFieldValue("repositoryRecordId", undefined)
    },
    [form, name]
  )

  const handleAutocompleteValueChange = useCallback(
    (value: string): void => {
      if (value.trim().length === 0) {
        onDuplicateCandidateForCreateNew?.(null)
      }
      form.setFieldValue(name, value)
      if (
        selectedRepositorySuggestionNameRef.current !== null &&
        selectedRepositorySuggestionNameRef.current !== value
      ) {
        resetRepositoryRecordSelection(value)
        onDuplicateCandidateForCreateNew?.(null)
      }
      form.validateField(name)
    },
    [
      form,
      name,
      onDuplicateCandidateForCreateNew,
      resetRepositoryRecordSelection,
    ]
  )

  const handleAutocompleteClear = useCallback((): void => {
    resetRepositoryRecordSelection("")
    onDuplicateCandidateForCreateNew?.(null)
    form.validateField(name)
  }, [
    form,
    name,
    onDuplicateCandidateForCreateNew,
    resetRepositoryRecordSelection,
  ])

  const handleOptionSelected = useCallback(
    (option: ProductionSiteAutocompleteOption): void => {
      if (isCreateAnywayOption(option)) {
        resetRepositoryRecordSelection(option.label)
        onDuplicateCandidateForCreateNew?.(null)
        form.validateField(name)
        return
      }

      const productionSite = option.suggestedProductionSite
      const businessRelationshipId =
        productionSite.parentCompany.businessRelationshipId
      const canLinkToRepositoryRecord = businessRelationshipId !== null

      selectedRepositorySuggestionNameRef.current = productionSite.name
      repositoryPrefillSnapshotRef.current = {
        parentCompany: form.values.parentCompany,
        internalCode: form.values.internalCode,
        street: form.values.street,
        city: form.values.city,
        postalCode: form.values.postalCode,
        longitude: form.values.longitude,
        latitude: form.values.latitude,
        countryIso3166: form.values.countryIso3166,
        registrationIds: form.values.registrationIds,
        performedActivities: form.values.performedActivities,
      }

      form.setFieldValue(
        "repositoryRecordId",
        canLinkToRepositoryRecord ? productionSite.id : undefined
      )
      form.setFieldValue(name, productionSite.name)
      if (businessRelationshipId !== null) {
        form.setFieldValue("parentCompany", businessRelationshipId)
      }
      form.setFieldValue("internalCode", productionSite.internalCode ?? "")
      form.setFieldValue("street", productionSite.street ?? "")
      form.setFieldValue("city", productionSite.city ?? "")
      form.setFieldValue("postalCode", productionSite.postalCode ?? "")
      form.setFieldValue(
        "longitude",
        toOptionalCoordinate(productionSite.longitude)
      )
      form.setFieldValue(
        "latitude",
        toOptionalCoordinate(productionSite.latitude)
      )
      form.setFieldValue("countryIso3166", productionSite.countryIso3166)
      form.setFieldValue(
        "registrationIds",
        productionSite.registrationIds.map((registrationId) => ({
          id: registrationId.id,
          registrationId: registrationId.registrationId,
          type: registrationId.type,
        }))
      )
      form.setFieldValue(
        "performedActivities",
        productionSite.performedActivities.map((activity) => activity.id)
      )
      onDuplicateCandidateForCreateNew?.(null)
      form.validate()
    },
    [
      form,
      isCreateAnywayOption,
      name,
      onDuplicateCandidateForCreateNew,
      resetRepositoryRecordSelection,
    ]
  )

  const fetchPage = useCallback(
    async ({ search }: { readonly search: string }) => {
      const response = await findRelatedProductionSitesByOrganizationId({
        search,
        pagination: {
          take: MAX_PRODUCTION_SITE_REPOSITORY_AUTOCOMPLETE_RESULTS,
          skip: 0,
        },
      })
      const suggestions = response.data.data ?? []
      const duplicateCandidate = findPrimaryVisibleSuggestionForSearch({
        suggestions,
        search,
      })
      onDuplicateCandidateForCreateNew?.(duplicateCandidate?.id ?? null)

      return {
        items: suggestions,
      }
    },
    [onDuplicateCandidateForCreateNew]
  )

  const renderOption = useCallback<
    NonNullable<AutocompleteProps["renderOption"]>
  >(
    ({ option }) => {
      if (isCreateAnywayOption(option)) {
        const term = option.label
        return (
          <Text size="sm" fw={600} lineClamp={2} py={2}>
            {`Create new: "${term}"`}
          </Text>
        )
      }
      if (!isProductionSiteSuggestionOption(option)) {
        const optionLabel = Reflect.get(option, "label")
        const optionValue = Reflect.get(option, "value")
        const fallbackLabel =
          typeof optionLabel === "string"
            ? optionLabel
            : typeof optionValue === "string"
              ? optionValue
              : ""
        return <Text size="sm">{fallbackLabel}</Text>
      }

      const productionSite = option.suggestedProductionSite
      return (
        <Stack gap={2} py={2}>
          <Text size="sm" fw={600} lineClamp={1}>
            {productionSite.name}
          </Text>
          <Text size="xs" c="dimmed" lineClamp={1}>
            {formatOptionText(productionSite)}
          </Text>
        </Stack>
      )
    },
    [isCreateAnywayOption, isProductionSiteSuggestionOption]
  )

  return (
    <LazyAutocomplete<
      FindRelatedProductionSitesByOrganizationIdResponseDto,
      ProductionSiteAutocompleteOption
    >
      fetchPage={fetchPage}
      toOption={toSuggestionOption}
      prependOptions={({ searchValue, remoteOptions }) => {
        const trimmedSearch = searchValue.trim()
        if (
          trimmedSearch.length <
          MIN_PRODUCTION_SITE_REPOSITORY_AUTOCOMPLETE_CHARACTERS
        ) {
          return []
        }
        if (
          !hasExactVisibleNameMatch({
            search: trimmedSearch,
            options: remoteOptions,
          })
        ) {
          return []
        }
        return [
          {
            value: PRODUCTION_SITE_CREATE_NEW_SENTINEL,
            label: trimmedSearch,
            kind: "createAnyway",
          },
        ]
      }}
      debounceMs={300}
      minChars={MIN_PRODUCTION_SITE_REPOSITORY_AUTOCOMPLETE_CHARACTERS}
      fetchOnFocus={false}
      size="xs"
      initialSearchValue={nameValue}
      onDebouncedSearchValueChange={handleAutocompleteValueChange}
      onOptionSelected={handleOptionSelected}
      onClear={handleAutocompleteClear}
      renderOption={renderOption}
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
          w="100%"
          justify="space-between"
        />
      }
      labelProps={{
        w: "100%",
      }}
      placeholder={placeholder}
      clearable
      error={error}
      disabled={disabled}
      limit={MAX_PRODUCTION_SITE_REPOSITORY_AUTOCOMPLETE_RESULTS}
      comboboxProps={COMBOBOX_PROPS}
    />
  )
}
